import React, { useState, useEffect, useCallback } from "react";
import {
  X, Fingerprint, RefreshCw, LogIn, LogOut, Clock,
  CalendarClock, Plus, Trash2, AlertTriangle, Save,
} from "lucide-react";
import api from "../utils/api";
import { toast } from "react-toastify";
import { useAuth } from "./authentication/AuthContext";

const thisMonth = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }).slice(0, 7);

const fmtTime = (d) =>
  d ? new Date(d).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "—";

const fmtHours = (minutes) => {
  if (!minutes) return "0h";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

export default function AttendancePanel({ onClose }) {
  const { isStaff, staffRole, staffName } = useAuth();
  const isManager = !isStaff || staffRole === "manager";

  // Punch state (staff)
  const [me, setMe] = useState(null);
  const [punching, setPunching] = useState(false);

  // Register state (manager)
  const [month, setMonth] = useState(thisMonth());
  const [register, setRegister] = useState([]);
  const [loading, setLoading] = useState(true);

  // Shifts (manager)
  const [shifts, setShifts] = useState([]);
  const [editingShifts, setEditingShifts] = useState(false);
  const [savingShifts, setSavingShifts] = useState(false);

  const fetchMe = useCallback(async () => {
    try {
      const res = await api.get("/attendance/me");
      setMe(res.data);
    } catch { /* silent */ }
  }, []);

  const fetchRegister = useCallback(async () => {
    if (!isManager) { setLoading(false); return; }
    setLoading(true);
    try {
      const [regRes, shiftRes] = await Promise.all([
        api.get(`/attendance/register?month=${month}`),
        api.get("/attendance/shifts"),
      ]);
      setRegister(regRes.data.register || []);
      setShifts(shiftRes.data.shifts || []);
    } catch {
      toast.error("Failed to load attendance register");
    } finally {
      setLoading(false);
    }
  }, [month, isManager]);

  useEffect(() => { fetchMe(); }, [fetchMe]);
  useEffect(() => { fetchRegister(); }, [fetchRegister]);

  const punch = async (action) => {
    setPunching(true);
    try {
      const res = await api.post(`/attendance/${action}`);
      toast.success(res.data.message);
      fetchMe();
      if (isManager) fetchRegister();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed");
    } finally {
      setPunching(false);
    }
  };

  const saveShifts = async () => {
    setSavingShifts(true);
    try {
      await api.put("/attendance/shifts", { shifts });
      toast.success("Shifts saved");
      setEditingShifts(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save shifts");
    } finally {
      setSavingShifts(false);
    }
  };

  const att = me?.attendance;
  const workedSoFar = att?.clockIn
    ? Math.round(((att.clockOut ? new Date(att.clockOut) : new Date()) - new Date(att.clockIn)) / 60000)
    : 0;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-teal-500/15 border border-teal-500/25 flex items-center justify-center">
              <Fingerprint size={15} className="text-teal-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800">Attendance & Shifts</h2>
              <p className="text-[10px] text-slate-400">Clock in/out · monthly register · payroll hours</p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
            <X size={14} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">
          {/* ── Punch card (staff only) ── */}
          {me?.isStaff && (
            <div className={`rounded-2xl border-2 p-4 flex items-center gap-4 ${att ? (att.clockOut ? "border-slate-200 bg-slate-50" : "border-emerald-300 bg-emerald-50/60") : "border-teal-300 bg-teal-50/60"}`}>
              <div className="flex-1">
                <p className="text-xs font-black text-slate-800">{staffName}</p>
                {!att ? (
                  <p className="text-[11px] text-slate-500 mt-0.5">Not clocked in yet today</p>
                ) : (
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    In {fmtTime(att.clockIn)}
                    {att.clockOut ? ` · Out ${fmtTime(att.clockOut)}` : " · working now"}
                    {" · "}{fmtHours(workedSoFar)}
                    {att.shiftName ? ` · ${att.shiftName}` : ""}
                    {att.isLate && <span className="text-amber-600 font-bold"> · {att.lateByMinutes} min late</span>}
                  </p>
                )}
              </div>
              {!att ? (
                <button onClick={() => punch("clock-in")} disabled={punching}
                  className="flex items-center gap-2 px-5 py-3 text-sm font-black bg-teal-600 hover:bg-teal-700 text-white rounded-xl disabled:opacity-50">
                  {punching ? <RefreshCw size={15} className="animate-spin" /> : <LogIn size={15} />} Clock In
                </button>
              ) : !att.clockOut ? (
                <button onClick={() => punch("clock-out")} disabled={punching}
                  className="flex items-center gap-2 px-5 py-3 text-sm font-black bg-rose-600 hover:bg-rose-700 text-white rounded-xl disabled:opacity-50">
                  {punching ? <RefreshCw size={15} className="animate-spin" /> : <LogOut size={15} />} Clock Out
                </button>
              ) : (
                <span className="text-xs font-bold text-slate-400">Day complete ✓</span>
              )}
            </div>
          )}

          {isManager && (
            <>
              {/* ── Shifts editor ── */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <CalendarClock size={11} /> Shifts
                  </p>
                  {!editingShifts ? (
                    <button onClick={() => setEditingShifts(true)} className="text-[10px] font-bold text-teal-600 hover:text-teal-700">Edit</button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingShifts(false); fetchRegister(); }} className="text-[10px] font-bold text-slate-400">Cancel</button>
                      <button onClick={saveShifts} disabled={savingShifts}
                        className="flex items-center gap-1 text-[10px] font-bold text-white bg-teal-600 px-2.5 py-1 rounded-lg disabled:opacity-50">
                        <Save size={10} /> Save
                      </button>
                    </div>
                  )}
                </div>

                {shifts.length === 0 && !editingShifts ? (
                  <p className="text-[11px] text-slate-400 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2.5">
                    No shifts defined — clock-ins won't be checked for lateness.{" "}
                    <button onClick={() => { setEditingShifts(true); setShifts([{ name: "Morning", start: "09:00", end: "17:00", graceMinutes: 15 }]); }} className="font-bold text-teal-600">Add your first shift</button>
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {shifts.map((s, i) => editingShifts ? (
                      <div key={i} className="flex items-center gap-2">
                        <input value={s.name} onChange={e => setShifts(sh => sh.map((x, n) => n === i ? { ...x, name: e.target.value } : x))}
                          placeholder="Shift name" className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500" />
                        <input type="time" value={s.start} onChange={e => setShifts(sh => sh.map((x, n) => n === i ? { ...x, start: e.target.value } : x))}
                          className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs" />
                        <span className="text-[10px] text-slate-400">to</span>
                        <input type="time" value={s.end} onChange={e => setShifts(sh => sh.map((x, n) => n === i ? { ...x, end: e.target.value } : x))}
                          className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs" />
                        <input type="number" min="0" value={s.graceMinutes} onChange={e => setShifts(sh => sh.map((x, n) => n === i ? { ...x, graceMinutes: e.target.value } : x))}
                          title="Grace minutes" className="w-14 border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-center" />
                        <button onClick={() => setShifts(sh => sh.filter((_, n) => n !== i))}
                          className="w-7 h-7 flex items-center justify-center rounded-lg bg-rose-50 text-rose-500 hover:bg-rose-100">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ) : (
                      <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-100 px-3 py-2">
                        <Clock size={13} className="text-teal-500" />
                        <span className="text-xs font-bold text-slate-700">{s.name}</span>
                        <span className="text-[11px] text-slate-500">{s.start} – {s.end}</span>
                        <span className="text-[10px] text-slate-400 ml-auto">{s.graceMinutes ?? 15} min grace</span>
                      </div>
                    ))}
                    {editingShifts && (
                      <button onClick={() => setShifts(sh => [...sh, { name: "", start: "09:00", end: "17:00", graceMinutes: 15 }])}
                        className="flex items-center gap-1 text-[10px] font-bold text-teal-600 hover:text-teal-700 mt-1">
                        <Plus size={11} /> Add shift
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* ── Monthly register ── */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">Monthly register</p>
                  <input type="month" value={month} onChange={e => setMonth(e.target.value)}
                    className="border border-slate-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-teal-500" />
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw size={16} className="animate-spin text-slate-400" />
                  </div>
                ) : register.length === 0 ? (
                  <p className="text-center text-xs text-slate-400 py-8">No staff or attendance records</p>
                ) : (
                  <div className="rounded-xl border border-slate-200 overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-50 text-[9px] font-black uppercase tracking-wider text-slate-400">
                          <th className="px-3 py-2 text-left">Staff</th>
                          <th className="px-3 py-2 text-right">Days present</th>
                          <th className="px-3 py-2 text-right">Late</th>
                          <th className="px-3 py-2 text-right">Total hours</th>
                          <th className="px-3 py-2 text-right">Avg / day</th>
                        </tr>
                      </thead>
                      <tbody>
                        {register.map(s => (
                          <tr key={s.staffId} className="border-t border-slate-100">
                            <td className="px-3 py-2">
                              <span className="font-bold text-slate-700">{s.name}</span>
                              <span className="text-[10px] text-slate-400 ml-1.5 uppercase">{s.role}</span>
                            </td>
                            <td className="px-3 py-2 text-right font-semibold">{s.daysPresent}</td>
                            <td className="px-3 py-2 text-right">
                              {s.lateCount > 0 ? (
                                <span className="inline-flex items-center gap-1 text-amber-600 font-bold">
                                  <AlertTriangle size={10} /> {s.lateCount}
                                </span>
                              ) : <span className="text-slate-300">0</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-black text-slate-800">{fmtHours(s.totalMinutes)}</td>
                            <td className="px-3 py-2 text-right text-slate-500">
                              {s.daysPresent > 0 ? fmtHours(Math.round(s.totalMinutes / s.daysPresent)) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-[10px] text-slate-400 mt-1.5">
                  Total hours = clock-in to clock-out. Use this as the payroll hours basis for the month.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200">Close</button>
        </div>
      </div>
    </div>
  );
}
